import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, History } from 'lucide-react';

import { Container } from '../layouts/Container';
import { selectUserToken } from '../redux/features/user/userSelectors';
import { ImportFlow } from '../features/parser-import/ImportFlow';
import { useListBatchesQuery } from '../features/parser-import/api/parserImportApi';

const STATUS_STYLES = {
  pending: 'bg-lightGrayColor text-darkGrayColor',
  running: 'bg-purpleColor/15 text-purpleColor',
  cancelling: 'bg-orangeColor/15 text-orangeColor',
  cancelled: 'bg-lightGrayColor text-darkGrayColor',
  completed: 'bg-greenColor/15 text-greenColor',
  completed_with_errors: 'bg-orangeColor/15 text-orangeColor',
  failed: 'bg-redColor/15 text-redColor',
};

const COLUMNS = [
  { key: 'id', label: '#', sortable: false },
  { key: 'created_at', label: 'Date', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'radius_km', label: 'Radius', sortable: false },
  { key: 'total', label: 'Total', sortable: true },
  { key: 'created', label: 'Created', sortable: false },
  { key: 'enriched', label: 'Enriched', sortable: false },
  { key: 'overwritten', label: 'Overwritten', sortable: false },
  { key: 'skipped', label: 'Skipped', sortable: false },
  { key: 'failed', label: 'Failed', sortable: false },
];

const fmtDate = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

// Return the sortable value for a batch row against a column key.
const readValue = (batch, key) => {
  if (key === 'created_at') return batch.created_at ?? '';
  if (key === 'status') return batch.status ?? '';
  if (key === 'total') return batch.counts?.total ?? 0;
  return 0;
};

// /settings/parser/imports — the past-batches admin table.
export const ImportHistory = () => {
  const token = useSelector(selectUserToken);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openBatchId, setOpenBatchId] = useState(null);

  const { data, isLoading, error } = useListBatchesQuery({
    token,
    page,
    per_page: 25,
  });

  const meta = data?.meta;

  const rows = useMemo(() => {
    const raw = data?.data ?? data ?? [];
    const copy = [...(Array.isArray(raw) ? raw : [])];
    copy.sort((a, b) => {
      const av = readValue(a, sortBy);
      const bv = readValue(b, sortBy);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, sortBy, sortOrder]);

  const totalPages = meta?.last_page ?? 1;

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
  };

  return (
    <Container>
      <div
        className="flex flex-col items-start gap-4 w-full bg-white"
        style={{ minHeight: 'calc(100vh - 5rem)' }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <History size={18} className="text-purpleColor" />
            <h1 className="text-lg font-semibold text-blackColor">
              Import history
            </h1>
          </div>
          <button
            type="button"
            onClick={() => navigate('/settings/parser/view')}
            className="text-xs text-purpleColor hover:underline"
          >
            ← Back to parser
          </button>
        </div>

        {isLoading && (
          <div className="flex flex-col gap-1 w-full">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded bg-lightGrayColor animate-pulse"
              />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="text-sm text-redColor">
            Failed to load import history.
          </div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div className="w-full py-12 text-center text-sm text-darkGrayColor">
            No import batches yet — run your first import from the Parser view.
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <>
            <div className="w-full overflow-auto">
              <table className="min-w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr className="border-b border-gray-200">
                    {COLUMNS.map((col) => {
                      const isActive = sortBy === col.key;
                      const Icon = !col.sortable
                        ? null
                        : isActive
                        ? sortOrder === 'asc'
                          ? ArrowUp
                          : ArrowDown
                        : ArrowUpDown;
                      return (
                        <th
                          key={col.key}
                          className="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                        >
                          <button
                            type="button"
                            disabled={!col.sortable}
                            onClick={() => col.sortable && handleSort(col.key)}
                            className={
                              'flex items-center gap-1 ' +
                              (col.sortable ? 'hover:text-gray-900' : 'cursor-default')
                            }
                          >
                            {col.label}
                            {Icon && <Icon size={12} />}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((batch) => (
                    <tr
                      key={batch.id}
                      onClick={() => setOpenBatchId(batch.id)}
                      className="border-b border-gray-100 hover:bg-lightestGrayColor cursor-pointer"
                    >
                      <td className="px-3 py-2 text-xs text-blackColor font-medium">
                        <div className="flex items-center gap-2">
                          <span>#{batch.id}</span>
                          {batch.initiator === 'auto' && (
                            <span className="px-1.5 py-0.5 rounded bg-purpleColor/10 text-purpleColor text-[10px] font-semibold" title={batch.import_rule?.name || 'Auto-import'}>
                              AUTO{batch.import_rule?.name ? ` · ${batch.import_rule.name}` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-darkGrayColor">
                        {fmtDate(batch.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            'px-2 py-0.5 rounded text-[10px] font-medium ' +
                            (STATUS_STYLES[batch.status] || STATUS_STYLES.pending)
                          }
                        >
                          {batch.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-blackColor">
                        {batch.radius_km ?? '—'} km
                      </td>
                      <td className="px-3 py-2 text-xs text-blackColor font-medium">
                        {batch.counts?.total ?? 0}
                      </td>
                      <td className="px-3 py-2 text-xs text-greenColor">
                        {batch.counts?.created ?? 0}
                      </td>
                      <td className="px-3 py-2 text-xs text-purpleColor">
                        {batch.counts?.enriched ?? 0}
                      </td>
                      <td className="px-3 py-2 text-xs text-orangeColor">
                        {batch.counts?.overwritten ?? 0}
                      </td>
                      <td className="px-3 py-2 text-xs text-darkGrayColor">
                        {batch.counts?.skipped ?? 0}
                      </td>
                      <td className="px-3 py-2 text-xs text-redColor">
                        {batch.counts?.failed ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2 w-full justify-end text-xs">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
                >
                  <ChevronLeft size={12} /> Prev
                </button>
                <span className="text-darkGrayColor">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 disabled:opacity-40"
                >
                  Next <ChevronRight size={12} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Read-only re-open of a past batch: mounts ImportFlow with initialBatchId. */}
      {openBatchId && (
        <ImportFlow
          isOpen={openBatchId != null}
          onClose={() => setOpenBatchId(null)}
          initialBatchId={openBatchId}
          readOnly
        />
      )}
    </Container>
  );
};
